import { createVuetify } from "vuetify";
import { aliases as vuetifyAliases, mdi } from "vuetify/iconsets/mdi-svg";
import { VTreeview } from "vuetify/labs/VTreeview";
import { VDateInput } from "vuetify/labs/VDateInput";
import { VBtn } from "vuetify/components/VBtn";

// To use an MDI icon, include it in the import and mdiIcons lists below. It
// will be aliased automatically. E.g., mdiAppsBox is available as
// icon="$apps-box".
// https://pictogrammers.com/library/mdi/
//
// For tree-shaking to work (so we don't include all 7,500 MDI icons in the
// bundle), this needs to be a selective import, so there's no way to avoid
// duplicate lists.

import {
    mdiAccountClock,
    mdiAccountLock,
    mdiAlert,
    mdiAlertCircle,
    mdiAppsBox,
    mdiArchive,
    mdiArchiveRemove,
    mdiArrowBottomRight,
    mdiArrowDownBoldCircle,
    mdiAtlassian,
    mdiAtom,
    mdiBackspace,
    mdiBookshelf,
    mdiBottleTonic,
    mdiCalendar,
    mdiCamera,
    mdiCancel,
    mdiCart,
    mdiCheck,
    mdiCheckCircle,
    mdiChevronDown,
    mdiChevronRight,
    mdiChevronUp,
    mdiClipboardEditOutline,
    mdiClipboardText,
    mdiClock,
    mdiClose,
    mdiCloseCircle,
    mdiCloseOctagon,
    mdiCloudUpload,
    mdiCodeJson,
    mdiCog,
    mdiCogs,
    mdiContentSave,
    mdiDatabaseSearch,
    mdiDelete,
    mdiDownload,
    mdiDownloadCircle,
    mdiEye,
    mdiFile,
    mdiFileDocument,
    mdiFileEye,
    mdiFlaskEmptyOutline,
    mdiFlaskOutline,
    mdiFolder,
    mdiFruitCherries,
    mdiMagnify,
    mdiMinusCircle,
    mdiOpenInNew,
    mdiPencil,
    mdiPencilCircle,
    mdiPlus,
    mdiPlusCircle,
    mdiQrcode,
    mdiReplay,
    mdiReload,
    mdiRadioboxBlank,
    mdiSigma,
    mdiTableArrowDown,
    mdiTableArrowUp,
    mdiTableCog,
    mdiTestTube,
    mdiTimerSand,
    mdiTools,
    mdiTrashCanOutline,
    mdiUpload,
    mdiUploadCircle,
    mdiViewGridCompact,
    mdiViewList,
    mdiWindowClose,
} from "@mdi/js";

const mdiIcons = {
    mdiAccountClock,
    mdiAccountLock,
    mdiAlert,
    mdiAlertCircle,
    mdiAppsBox,
    mdiArchive,
    mdiArchiveRemove,
    mdiArrowBottomRight,
    mdiArrowDownBoldCircle,
    mdiAtlassian,
    mdiAtom,
    mdiBookshelf,
    mdiBottleTonic,
    mdiBackspace,
    mdiCalendar,
    mdiCamera,
    mdiCancel,
    mdiCart,
    mdiCheck,
    mdiCheckCircle,
    mdiChevronDown,
    mdiChevronRight,
    mdiChevronUp,
    mdiClipboardEditOutline,
    mdiClipboardText,
    mdiClock,
    mdiClose,
    mdiCloseCircle,
    mdiCloseOctagon,
    mdiCloudUpload,
    mdiCodeJson,
    mdiCog,
    mdiCogs,
    mdiContentSave,
    mdiDatabaseSearch,
    mdiDelete,
    mdiDownload,
    mdiDownloadCircle,
    mdiEye,
    mdiFile,
    mdiFileDocument,
    mdiFileEye,
    mdiFlaskEmptyOutline,
    mdiFlaskOutline,
    mdiFolder,
    mdiFruitCherries,
    mdiMagnify,
    mdiMinusCircle,
    mdiOpenInNew,
    mdiPencil,
    mdiPencilCircle,
    mdiPlus,
    mdiPlusCircle,
    mdiQrcode,
    mdiRadioboxBlank,
    mdiReload,
    mdiReplay,
    mdiSigma,
    mdiTableArrowDown,
    mdiTableArrowUp,
    mdiTableCog,
    mdiTestTube,
    mdiTimerSand,
    mdiTools,
    mdiTrashCanOutline,
    mdiUpload,
    mdiUploadCircle,
    mdiViewGridCompact,
    mdiViewList,
    mdiWindowClose,
};

const mdiAliases: Record<string, string> = {};
for (const iconName of Object.keys(mdiIcons)) {
    const key = iconName.replace(/^mdi/, "");
    const alias = `${key
        .replace(/([A-Z])/g, "-$&")
        .toLowerCase()
        .slice(1)}`;
    mdiAliases[alias] = mdiIcons[iconName];
}

export default createVuetify({
    icons: {
        aliases: {
            ...vuetifyAliases,
            ...mdiAliases,
        },
        sets: {
            mdi,
        },
    },
    components: {
        VDateInput,
        VTreeview,
    },
    aliases: {
        VIconBtn: VBtn,
    },
    defaults: {
        VAutocomplete: { variant: "underlined" },
        VCheckbox: { color: "primary" },
        VDateInput: { variant: "underlined" },
        VTextField: { variant: "underlined" },
        VSelect: { variant: "underlined" },
        VFileInput: { variant: "underlined" },
        VSwitch: { color: "primary" },
        VDataTable: { hover: true },
        VIconBtn: { variant: "plain", icon: "true" },
        VBtn: { variant: "elevated" },
        VCardActions: {
            // someone has to explain to me why this is necessary given VBtn's global
            // variant is already "elevated"
            VBtn: {
                variant: "elevated",
            },
        },
        VRadioGroup: { color: "primary" },
    },
    theme: {
        themes: {
            dark: {
                colors: {
                    primary: "#7266BA",
                    secondary: "#5B8770",
                    accent: "#407887",
                    error: "#FF5252",
                    info: "#2196F3",
                    success: "#4CAF50",
                    warning: "#F18636",
                    amber: "#7266BA",
                },
            },
            light: {
                colors: {
                    info: "#027545",
                    darkgreen: "#005a36",
                },
            },
        },
    },
});
